import { Badge, severityToBadgeVariant } from '@00start/ui';
import type { UnifiedFinding } from '../types';

interface FindingsTableProps {
  findings: UnifiedFinding[];
}

// Simple tier → severity mapping for the Badge variant. Aviation findings
// use Firebreak's tier system (C = critical, B = moderate, A = good, R = resolved).
function tierToSeverity(tier: string): 'critical' | 'high' | 'medium' | 'low' {
  switch (tier) {
    case 'C': return 'critical';
    case 'B': return 'medium';
    case 'A': return 'low';
    default:  return 'low';
  }
}

export function FindingsTable({ findings }: FindingsTableProps) {
  return (
    <div
      className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700"
      data-testid="aviation-demo-findings-table"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-slate-800/80">
            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              zone
            </th>
            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              tier
            </th>
            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              finding
            </th>
            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              citation
            </th>
            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              jurisdiction
            </th>
            <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              method
            </th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f) => {
            const c = f.compliance;
            return (
              <tr
                key={`${f.sourceZone}-${f.code}`}
                className="border-t border-gray-100 dark:border-slate-800"
                data-testid={`aviation-demo-finding-${f.sourceZone}`}
              >
                <td className="px-4 py-2 text-gray-900 dark:text-slate-100">{f.sourceZone}</td>
                <td className="px-4 py-2">
                  <Badge variant={severityToBadgeVariant(tierToSeverity(f.tier))} dot size="sm">
                    {f.tier}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-slate-300">
                  {f.title ?? f.code}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-slate-100">
                  {c?.codeSection || '—'}
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-slate-300">
                  {c?.jurisdiction || '—'}
                </td>
                <td className="px-4 py-2">
                  {c?.method ? (
                    <Badge
                      variant={c.method === 'deterministic' ? 'success' : 'warning'}
                      dot size="sm"
                    >
                      {c.method}
                    </Badge>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
